// apps/app/src/state/notifications/index.ts
import { createInfiniteQuery, createMutation, createQuery, useQueryClient } from '@tanstack/solid-query'
import { createSignal } from 'solid-js'
import { userId } from '../auth'
import {
  handleClearAllNotifications,
  handleGetNotifications,
  handleGetUnreadNotifications,
  handleMarkNotificationRead,
} from './resources'

export function useNotifications() {
  const queryClient = useQueryClient()
  const [newNotifications, setNewNotifications] = createSignal<boolean>(false)

  // Fetch notifications using Infinite Query
  const notificationsQuery = createInfiniteQuery(() => ({
    queryKey: ['notifications', userId()],
    queryFn: ({ pageParam }) => handleGetNotifications(userId()!, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!userId(),
  }))

  const markNotificationReadMutation = createMutation(() => ({
    mutationFn: (id: string) => handleMarkNotificationRead(id),
    onSuccess: (_, id) => {
      // Optimistically remove the notification from the unread list for instant UI feedback
      queryClient.setQueryData(['notifications', 'unread', userId()], (old: any) =>
        old ? old.filter((n: any) => n.id !== id) : []
      )

      // Invalidate both lists to ensure they are fully synced with the database
      queryClient.invalidateQueries({ queryKey: ['notifications', userId()] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread', userId()] })
    },
  }))

  const clearAllNotificationsMutation = createMutation(() => ({
    mutationFn: () => handleClearAllNotifications(userId()!),
    onSuccess: () => {
      // Instantly clear the unread cache
      queryClient.setQueryData(['notifications', 'unread', userId()], [])

      queryClient.invalidateQueries({ queryKey: ['notifications', userId()] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread', userId()] })
    },
  }))

  const unreadNotificationsQuery = createQuery(() => ({
    queryKey: ['notifications', 'unread', userId()],
    queryFn: () => handleGetUnreadNotifications(userId()!),
    enabled: !!userId(),
    initialData: [],
  }))

  return {
    newNotifications,
    setNewNotifications,
    notificationsQuery,
    markNotificationReadMutation,
    clearAllNotificationsMutation,
    unreadNotificationsQuery
  }
}
